import { createSlice } from "@reduxjs/toolkit";
import {
  dashboarUserStatusListsApis,
  dashboardActiveTasksApis,
  dashboardClientStatusListsApis,
  dashboardQueriesCountAndProductivityStarApis,
  dashboardQueryTypeListsApis,
  dashboardSummaryListsApis,
} from "./dashboardActions";
import { clockInEmployee, clockOutEmployee, getWorkingHours, getAttendanceDashboard, getStatusClock, getTeamClockStatus, getBreakBreakdown} from "./dashboardActions";
// import { workingHoursApi } from "./dashboardApi";

const initialState = {
  clientStatusData: [],
  queryTypeData: [],
  summaryData: [],
  userStatusData: [],
  activeTasks: [],
  activeTasksStatus: "idle",
  queriesCountAndProductivityData: {}, // <-- FIXED: Must be an object {}
  status: "idle",
  error: null,
  loading: false,
  clockInData: null,
  clockOutData: null,
  workingHoursData: null,
  attendanceDashboardData: null,
  clockStatus: "Clocked Out",
  clockStatusLoading: false,
  clockStatusError: null,
  /* Null until /dashboard/clock-status answers — the menu shows nothing rather
     than guessing "Active" and then correcting itself a moment later. */
  workStatus: null,
  lastLoginAt: null,
  /* The whole team's clock. Counts come from the server beside the rows so
     the panel's tab labels and its list cannot drift apart. */
  teamClock: { rows: [], counts: { in: 0, out: 0, notIn: 0 }, total: 0 },
  teamClockLoading: false,
  /* Break Hours by reason. Null until it answers, so the card shows the plain
     total rather than three confident zeroes it has not been told. */
  breakBreakdown: null,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboardData(state) {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder

    .addCase(getStatusClock.pending, (state) => {
      state.clockStatusLoading = true;
      state.clockStatusError = null;
    })
    .addCase(getStatusClock.fulfilled, (state, action) => {
      state.clockStatusLoading = false;
      state.clockStatus = action.payload?.data?.status || "Clocked Out";
      /* The live work status, straight from tbl_User. The header menu reads
         this rather than the login payload in localStorage, which is written
         once at sign-in and then never again. */
      state.workStatus = action.payload?.data?.WorkStatus || null;
      /* When they signed in — a different fact from when their clock started,
         and kept apart so neither has to stand in for the other. */
      state.lastLoginAt = action.payload?.data?.LastLoginAt || null;
    })
    .addCase(getStatusClock.rejected, (state, action) => {
      state.clockStatusLoading = false;
      state.clockStatusError = action.payload?.message ||
      "Unable to get clock status"
    })

    .addCase(getTeamClockStatus.pending, (state) => {
      state.teamClockLoading = true;
    })
    .addCase(getTeamClockStatus.fulfilled, (state, action) => {
      state.teamClockLoading = false;
      /* successHandler wraps the payload once and axios wraps it again, and
         which of the two levels carries it has differed by endpoint before —
         so read whichever one actually holds `rows`. */
      const body = action.payload?.data ?? action.payload;
      const inner = body?.data ?? body;
      state.teamClock = {
        rows: Array.isArray(inner?.rows) ? inner.rows : [],
        counts: inner?.counts ?? { in: 0, out: 0, notIn: 0 },
        total: inner?.total ?? 0,
      };
    })
    .addCase(getBreakBreakdown.fulfilled, (state, action) => {
      const body = action.payload?.data ?? action.payload;
      const inner = body?.data ?? body;
      state.breakBreakdown = inner?.totals ?? null;
    })
    .addCase(getBreakBreakdown.rejected, (state) => {
      // Left alone: the card falls back to the single total it always had.
    })

    .addCase(getTeamClockStatus.rejected, (state) => {
      state.teamClockLoading = false;
      // Left as it was rather than blanked: an empty roster reads as
      // "nobody is here", which is a stronger claim than "the call failed".
    })

      // --- Client Status ---
      .addCase(dashboardClientStatusListsApis.fulfilled, (state, action) => {
        state.status = "succeeded";
        const responseData = action.payload?.data?.data || action.payload?.data || [];
        state.clientStatusData = Array.isArray(responseData) ? responseData : [];
      })
      .addCase(dashboardClientStatusListsApis.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(dashboardClientStatusListsApis.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // --- Query Type ---
      .addCase(dashboardQueryTypeListsApis.fulfilled, (state, action) => {
        state.status = "succeeded";
        const responseData = action.payload?.data?.data || action.payload?.data || [];
        state.queryTypeData = Array.isArray(responseData) ? responseData : [];
      })
      .addCase(dashboardQueryTypeListsApis.pending, (state) => {
        state.status = "loading";
      })
      .addCase(dashboardQueryTypeListsApis.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // --- Summary ---
      .addCase(dashboardSummaryListsApis.fulfilled, (state, action) => {
        state.status = "succeeded";
        const responseData = action.payload?.data?.data || action.payload?.data || [];
        state.summaryData = Array.isArray(responseData) ? responseData : [];
      })
      .addCase(dashboardSummaryListsApis.pending, (state) => {
        state.status = "loading";
      })
      .addCase(dashboardSummaryListsApis.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // --- Active tasks (what is running right now) ---
      .addCase(dashboardActiveTasksApis.pending, (state) => {
        state.activeTasksStatus = "loading";
      })
      .addCase(dashboardActiveTasksApis.fulfilled, (state, action) => {
        state.activeTasksStatus = "succeeded";
        const rows = action.payload?.data?.data || action.payload?.data || [];
        state.activeTasks = Array.isArray(rows) ? rows : [];
      })
      .addCase(dashboardActiveTasksApis.rejected, (state) => {
        state.activeTasksStatus = "failed";
      })

      // --- User Status ---
      .addCase(dashboarUserStatusListsApis.fulfilled, (state, action) => {
        state.status = "succeeded";
        const responseData = action.payload?.data?.data || action.payload?.data || [];
        state.userStatusData = Array.isArray(responseData) ? responseData : [];
      })
      .addCase(dashboarUserStatusListsApis.pending, (state) => {
        state.status = "loading";
      })
      .addCase(dashboarUserStatusListsApis.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // --- Queries Count & Productivity Star (FIXED) ---
      .addCase(dashboardQueriesCountAndProductivityStarApis.fulfilled, (state, action) => {
        state.status = "succeeded";

        // Safely extract the nested object. Do NOT force it into an array!
        const rData = action.payload?.data;
        const level1 = rData?.data;

        // Save it directly into the state variable
        state.queriesCountAndProductivityData = level1 || rData || {};
      })
      .addCase(dashboardQueriesCountAndProductivityStarApis.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(dashboardQueriesCountAndProductivityStarApis.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(clockInEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(clockInEmployee.fulfilled, (state, action) => {
        state.loading = false;
        state.clockInData = action.payload.data.data;
      })

      .addCase(clockInEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(clockOutEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(clockOutEmployee.fulfilled, (state, action) => {
        state.loading = false;
        state.clockOutData = action.payload.data.data;
      })

      .addCase(clockOutEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getWorkingHours.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(getWorkingHours.fulfilled, (state, action) => {
        state.loading = false;
        state.workingHoursData = action.payload.data.data;
      })

      .addCase(getWorkingHours.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // --- Attendance Dashboard (Clock-in summary card) ---
      .addCase(getAttendanceDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAttendanceDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.attendanceDashboardData = action.payload?.data?.data || null;
      })
      .addCase(getAttendanceDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
  },
});

export const { clearDashboardData } = dashboardSlice.actions;
export default dashboardSlice.reducer;